/**
 * The top bar's breadcrumb (mission 6 WP05 T032; FR-003; style guide §7.17).
 *
 * Renders the `handle.crumb` segments of every matched route, parent first,
 * exactly as routes.tsx typed them (register 17): this component spells no
 * label. Every segment but the last links when the handle gives it a `to`; the
 * last is the current page. A run's short id renders in mono (§3). With no
 * crumb on any match, nothing renders.
 */
import { Link, useMatches } from 'react-router-dom'

import { cn } from '@/components/ui/cn'
import type { CrumbSegment, RouteHandle } from '../routes'

function hasCrumb(handle: unknown): handle is RouteHandle {
  return typeof (handle as Partial<RouteHandle> | undefined)?.crumb === 'function'
}

export function Breadcrumb() {
  const segments: CrumbSegment[] = useMatches().flatMap((m) => (hasCrumb(m.handle) ? m.handle.crumb(m.params) : []))
  if (segments.length === 0) return null

  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex items-center text-body-sm text-navy-600">
        {segments.map((segment, i) => {
          const last = i === segments.length - 1
          const mono = segment.mono ? 'font-mono' : undefined
          return (
            <li key={`${i}-${segment.label}`} className="flex items-center">
              {i > 0 ? (
                <span aria-hidden="true" className="px-2">
                  ›
                </span>
              ) : null}
              {last ? (
                <span aria-current="page" className={cn('font-semibold text-navy-950', mono)}>
                  {segment.label}
                </span>
              ) : segment.to ? (
                <Link
                  to={segment.to}
                  className={cn(
                    'rounded-sm hover:text-navy-950 hover:underline focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]',
                    mono,
                  )}
                >
                  {segment.label}
                </Link>
              ) : (
                <span className={mono}>{segment.label}</span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
